/**
 * The moment's byline and sentence blocks. "Original" and "Generated"
 * appear only when both sentences exist. An empty teacher note stays an
 * add-message slot.
 */
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { type FamilyNoteSource } from "#utils/standoutMomentMigration"

import MomentEditPopover from "./MomentEditPopover"
import { REVIEW_MUTED } from "./reviewCardLayout"

/** Byline and share controls. The controls wrap under the byline as one row. */
export const itemBylineRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
  width: "100%",
} as const

export const itemShareRowStyle = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 8,
  flex: "0 0 auto",
  marginLeft: "auto",
  maxWidth: "100%",
} as const

/** Content kind, at the top of an item so the card reads as that thing. */
export const ItemKindTitle = ({ children }: { children: string }) => (
  <div
    data-testid="sr-item-kind"
    style={{
      flex: "1 1 auto",
      minWidth: 0,
      fontSize: 13,
      fontWeight: 600,
      lineHeight: "18px",
    }}
  >
    {children}
  </div>
)

/**
 * Who made it, and when, at the bottom right. `verb` is "written by" or
 * "taken by" so the name is not a bare label.
 */
export const ItemAttribution = ({
  verb,
  name,
  when,
}: {
  verb: "written by" | "taken by"
  name: string
  when: string
}) => (
  <div
    data-testid="sr-item-byline"
    style={{
      alignSelf: "flex-end",
      maxWidth: "100%",
      display: "flex",
      flexDirection: "column",
      textAlign: "right",
      fontSize: 12,
      lineHeight: "16px",
      color: REVIEW_MUTED,
      overflowWrap: "break-word",
    }}
  >
    {name ? (
      <div data-testid="sr-item-byline-name">
        {verb} {name}
      </div>
    ) : null}
    {when ? <div data-testid="sr-item-byline-when">{when}</div> : null}
  </div>
)

type SentenceEdit = {
  note: string | null | undefined
  onSave: (patch: { note: string | null }) => Promise<void>
  editLabel: string
  placeholder?: string
  buttonTestId?: string
  noteTestId?: string
  saveTestId?: string
}

/**
 * One sentence in its own box, with its pencil in the box's corner. When
 * both sentences exist, the one parents get has the highlight background.
 */
const ReviewSentenceBlock = ({
  label,
  labelTestId,
  textTestId,
  text,
  selected,
  choosing,
  selectable,
  onSelect,
  edit,
  locked,
  lockedReason,
}: {
  label: string
  labelTestId: string
  textTestId: string
  text: string
  selected: boolean
  /** Both sentences exist, so the selected one is highlighted. */
  choosing: boolean
  selectable: boolean
  onSelect: () => void
  edit: SentenceEdit | null
  locked: boolean
  lockedReason?: string
}) => {
  const theme = useThemeContext()
  const highlighted = choosing && selected
  const sentence =
    label || text ? (
      <div
        role={choosing ? "button" : undefined}
        tabIndex={selectable ? 0 : undefined}
        aria-pressed={choosing ? selected : undefined}
        aria-disabled={choosing && !selectable ? true : undefined}
        onClick={selectable ? onSelect : undefined}
        onKeyDown={
          selectable
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  onSelect()
                }
              }
            : undefined
        }
        style={{
          cursor: selectable ? "pointer" : choosing ? "not-allowed" : undefined,
          minWidth: 0,
        }}
      >
        {label ? (
          <div
            data-testid={labelTestId}
            style={{
              fontSize: 11,
              fontWeight: 700,
              lineHeight: "16px",
              color: REVIEW_MUTED,
            }}
          >
            {label}
          </div>
        ) : null}
        {text ? (
          <div data-testid={textTestId} style={{ whiteSpace: "pre-wrap" }}>
            {text}
          </div>
        ) : null}
      </div>
    ) : null
  return (
    <div
      data-testid="sr-share-choice"
      data-selected={selected ? "true" : "false"}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "6px 8px",
        borderRadius: 6,
        border: `1px solid ${highlighted ? theme.highlight : theme.gray[4]}`,
        backgroundColor: highlighted ? theme.highlight : "transparent",
        minWidth: 0,
      }}
    >
      {edit ? (
        <MomentEditPopover
          note={edit.note}
          onSave={edit.onSave}
          placeholder={edit.placeholder}
          buttonTestId={edit.buttonTestId}
          noteTestId={edit.noteTestId}
          saveTestId={edit.saveTestId}
          editLabel={edit.editLabel}
          leading={sentence}
          placeEditorBelow
          disabled={locked}
          disabledReason={lockedReason}
        />
      ) : (
        sentence
      )}
    </div>
  )
}

export const ItemSentenceStack = ({
  teacherNote,
  generatedSentence,
  chooseShareText,
  shareTextChoice,
  canChoose,
  isEditable,
  locked = false,
  lockedReason,
  busy = false,
  note,
  generatedNote,
  onSelect,
  onSaveNote,
  onSaveGenerated,
}: {
  teacherNote: string
  generatedSentence: string
  chooseShareText: boolean
  shareTextChoice: FamilyNoteSource
  canChoose: boolean
  isEditable: boolean
  /** Shared: the words and the choice wait until the item is unshared. */
  locked?: boolean
  /** Tooltip on a locked pencil. Defaults to "Unshare to edit". */
  lockedReason?: string
  /** The sentence choice is saving; another pick waits for it. */
  busy?: boolean
  note: string | null | undefined
  generatedNote: string | null | undefined
  onSelect: (next: FamilyNoteSource) => void
  onSaveNote: (patch: { note: string | null }) => Promise<void>
  onSaveGenerated: (patch: { note: string | null }) => Promise<void>
}) => (
  <div
    data-testid={chooseShareText ? "sr-share-text" : undefined}
    data-locked={locked ? "true" : "false"}
    aria-busy={busy || undefined}
    style={{ display: "flex", flexDirection: "column", gap: 8 }}
  >
    {teacherNote || isEditable ? (
      <ReviewSentenceBlock
        label={chooseShareText ? "Original" : ""}
        labelTestId="sr-original-label"
        textTestId="sr-moment-note"
        text={teacherNote}
        selected={shareTextChoice === "note"}
        choosing={chooseShareText}
        selectable={chooseShareText && canChoose && !locked && !busy}
        onSelect={() => onSelect("note")}
        locked={locked}
        lockedReason={lockedReason}
        edit={
          isEditable
            ? { note, onSave: onSaveNote, editLabel: "Edit original note" }
            : null
        }
      />
    ) : null}
    {generatedSentence ? (
      <ReviewSentenceBlock
        label={chooseShareText ? "Generated" : ""}
        labelTestId="sr-generated-label"
        textTestId="sr-moment-generated-note"
        text={generatedSentence}
        selected={shareTextChoice === "generatedNote"}
        choosing={chooseShareText}
        selectable={chooseShareText && canChoose && !locked && !busy}
        onSelect={() => onSelect("generatedNote")}
        locked={locked}
        lockedReason={lockedReason}
        edit={
          isEditable
            ? {
                note: generatedNote,
                onSave: onSaveGenerated,
                editLabel: "Edit generated note",
                placeholder: "Generated sentence",
                buttonTestId: "sr-edit-generated-button",
                noteTestId: "sr-edit-generated-note",
                saveTestId: "sr-edit-generated-save",
              }
            : null
        }
      />
    ) : null}
  </div>
)
